import { SAVE_KEY, WORLD_VERSION } from "../config";
import type { World } from "../engine/world";
import type { Player } from "./player";
import type { Containers } from "./containers";

export interface SaveData {
  version: number;
  seed: number;
  player: {
    x: number;
    y: number;
    z: number;
    yaw: number;
    pitch: number;
    flying: boolean;
    thirdPerson: boolean;
    health: number;
    hunger: number;
    inventory: unknown;
  };
  edits: [string, [number, number][]][];
  containers: unknown;
}

export function collectSave(world: World, player: Player, containers: Containers): SaveData {
  return {
    version: WORLD_VERSION,
    seed: world.seed,
    player: {
      x: player.x,
      y: player.y,
      z: player.z,
      yaw: player.yaw,
      pitch: player.pitch,
      flying: player.flying,
      thirdPerson: player.thirdPerson,
      health: player.health,
      hunger: player.hunger,
      inventory: player.inventory.serialize(),
    },
    edits: [...world.edits.entries()].map(([k, m]) => [k, [...m.entries()]]),
    containers: containers.serialize(),
  };
}

export function persist(data: SaveData): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(data));
  } catch {
    /* quota */
  }
}

export function loadSave(): SaveData | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as SaveData;
    if (data.version !== WORLD_VERSION) return null;
    return data;
  } catch {
    return null;
  }
}

export function applySave(data: SaveData, world: World, player: Player, containers: Containers): void {
  world.edits.clear();
  for (const [k, entries] of data.edits) {
    world.edits.set(k, new Map(entries));
  }
  player.x = data.player.x;
  player.y = data.player.y;
  player.z = data.player.z;
  player.yaw = data.player.yaw;
  player.pitch = data.player.pitch;
  player.flying = data.player.flying;
  player.thirdPerson = data.player.thirdPerson;
  player.health = data.player.health;
  player.hunger = data.player.hunger;
  player.inventory.load(data.player.inventory as never);
  containers.load(data.containers as never);
}

export function clearSave(): void {
  localStorage.removeItem(SAVE_KEY);
}
